import axios from "axios";
import { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Breadcrumbs from "../../components/breadcrumbs/Breadcrumbs";
import config from "../../util/config";
import CollegeCards from "./components/CollegeCard";
import { SortBy } from "./components/SortBy";
import type { SVGProps } from "react";
import StreamTabs from "./components/StreamTabs";
import DesktopFilterSidebar from "./components/DesktopFilterSidebar";
import MobileFilterSidebar from "./components/MobileFilterSidebar";

export function CharmTick(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      {...props}
      width="14"
      height="12"
      viewBox="0 0 14 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 opacity-0 peer-checked:opacity-100"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12.7554 0.971074C13.1039 1.29115 13.1342 1.8412 12.8231 2.19968L4.76617 11.4812C4.60566 11.6661 4.37612 11.7718 4.13513 11.7718C3.89414 11.7718 3.66459 11.6661 3.50409 11.4812L0.281339 7.76856C-0.0298314 7.41006 0.000443473 6.86002 0.348961 6.54004C0.697467 6.21994 1.23226 6.25103 1.54343 6.60953L4.13513 9.59516L11.561 1.04063C11.8721 0.682153 12.4069 0.651013 12.7554 0.971074Z"
        fill="currentColor"
      />
    </svg>
  );
}
export function SolarSortByTimeLinear(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={24}
      height={24}
      viewBox="0 0 24 24"
      {...props}
    >
      <g fill="none" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" d="M10 7H2m6 5H2m8 5H2"></path>
        <circle cx={17} cy={12} r={5}></circle>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M17 10v1.846L18 13"
        ></path>
      </g>
    </svg>
  );
}

export function MynauiSearch(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={24}
      height={24}
      viewBox="0 0 24 24"
      {...props}
    >
      <path
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M19 11.5a7.5 7.5 0 1 1-15 0a7.5 7.5 0 0 1 15 0m-2.107 5.42l3.08 3.08"
      ></path>
    </svg>
  );
}
export function JamFilter(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={24}
      height={24}
      viewBox="-2 -2 24 24"
      {...props}
    >
      <path
        fill="currentColor"
        d="m2.08 2l6.482 8.101A2 2 0 0 1 9 11.351V18l2-1.5v-5.15a2 2 0 0 1 .438-1.249L17.92 2zm0-2h15.84a2 2 0 0 1 1.561 3.25L13 11.35v5.15a2 2 0 0 1-.8 1.6l-2 1.5A2 2 0 0 1 7 18v-6.65L.519 3.25A2 2 0 0 1 2.08 0"
      ></path>
    </svg>
  );
}
export function LsiconRightOutline(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={20}
      height={20}
      viewBox="0 0 16 16"
      {...props}
    >
      <path
        fill="none"
        stroke="currentColor"
        d="M6 4.5L9.5 8L6 11.5"
        strokeWidth={1}
      ></path>
    </svg>
  );
}
interface College {
  collegeId: number;
  college_title: string;
  college_name: string;
  location: string;
  type: string;
  imageUrl: string;
  broucherUrl: string;
  isDataAvailable: string;
  details: {
    courses: string[];
    exams: string[];
  };
}

interface FilterOption {
  name: string;
  key: string;
  count: number;
}

interface FilterCategory {
  categoryKey: string;
  categoryName?: string;
  options: FilterOption[];
}

interface Stream {
  name: string;
  title: string;
}

interface SelectedFilters {
  [key: string]: string[];
}
const sortOptions = [
  { label: "A to Z", value: "atoz" },
  { label: "Z to A", value: "ztoa" },
  { label: "Featured Colleges", value: "featured_colleges" },
  { label: "Direct Admission", value: "direct_admission" },
];

export default function CollegeIntermediate() {
  const navigate = useNavigate();
  const [colleges, setColleges] = useState<College[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [openAccordions, setOpenAccordions] = useState<Record<string, boolean>>(
    {}
  );
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [activeMobileCategory, setActiveMobileCategory] =
    useState<string>("universities");

  const [filters, setFilters] = useState<FilterCategory[]>([]);
  const [streams, setStreams] = useState<Stream[]>([]);
  const [activeStream, setActiveStream] = useState<string>("");
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilters>({});
  const [searchKeyword, setSearchKeyword] = useState<string>("");
  const [searchTrigger, setSearchTrigger] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [sortBy, setSortBy] = useState("featured_colleges");
  const [searchStates, setSearchStates] = useState<Record<string, string>>({});
  const [showMoreStates, setShowMoreStates] = useState<Record<string, boolean>>(
    {}
  );

  useEffect(() => {
    const appliedFilters = Object.entries(selectedFilters).reduce(
      (acc, [key, val]) => {
        console.log("key is ", key, val);
        // if (key === "city"){
        //   if (Array.isArray(val) && val.length > 0) {
        //     const value = Array.from(new Set(val.map((v) => v.trim())));
        //     acc[key] = value
        //   }
        //   return acc;

        // } else {
        const value = val[0];
        if (Array.isArray(val) && val.length > 0) {
          // acc[key] = value;
          acc[key] = val.length > 1 ? val : val[0]; // handle both single & multi
        }
        return acc;
        // }
      },
      {} as Record<string, string[] | string>
    );

    console.log("applied filters are ", appliedFilters);

    const payload = {
      sortBy,
      stream: activeStream || "",
      offset: (currentPage - 1) * 10,
      fetchCount: 10,
      ...(searchKeyword.trim() && { search: searchKeyword.trim() }),
      ...appliedFilters,
    };

    setLoading(true);
    axios
      .post(`${config.apiUrl}/api/college/college/intermediate-page`, payload)
      .then((res) => {
        const data = res.data.data;
        setColleges(
          (data.data || []).map((item: any) => ({
            ...item,
            college_name:
              item.college_name ||
              item.college_title.replace(/\s+/g, "-").toLowerCase(),
            broucherUrl: item.broucherUrl || "",
            isDataAvailable: item.isDataAvailable || "1",
            details: {
              ...item.details,
              additional_info: item.details?.additional_info || {
                rankings: {},
                accreditations: [],
              },
            },
          }))
        );

        setFilters(data.filters || []);
        setStreams(data.streams || []);
        setTotalCount(data.filteredCollegeCount || data.totalCollegeCount || 0);
      })
      .catch((err) => {
        console.error("API error:", err);
      })
      .finally(() => setLoading(false));
  }, [activeStream, currentPage, searchTrigger, selectedFilters, sortBy]);
  const totalPages = Math.ceil(totalCount / 10);
  const toggleAccordion = (categoryKey: string) => {
    setOpenAccordions((prev) => ({
      ...prev,
      [categoryKey]: !prev[categoryKey],
    }));
  };
  useEffect(() => {
    const initialFilters: SelectedFilters = {};
    const initialAccordionState: Record<string, boolean> = {};

    searchParams.forEach((value, key) => {
      const trimmedValue = value.trim();
      if (initialFilters[key]) {
        initialFilters[key].push(trimmedValue);
      } else {
        initialFilters[key] = [trimmedValue];
      }
      initialAccordionState[key] = true;
    });
    if (filters.length > 0 && !Object.keys(initialAccordionState).length) {
      initialAccordionState[filters[0].categoryKey] = true;
    }
    setSelectedFilters(initialFilters);
    setOpenAccordions(initialAccordionState);
  }, []);

  const handleFilterChange = (category: string, key: string) => {
    setCurrentPage(1);

    setSelectedFilters((prev) => {
      const prevValues = prev[category] || [];
      const updated = prevValues.includes(key)
        ? prevValues.filter((val) => val !== key)
        : [...prevValues, key];

      const newFilters = {
        ...prev,
        [category]: updated,
      };

      // Update URL
      const updatedParams = new URLSearchParams(searchParams);
      updatedParams.delete(category);

      if (updated.length > 0) {
        updated.forEach((value) => {
          updatedParams.append(category, value);
        });
      }

      setSearchParams(updatedParams);

      return newFilters;
    });
  };
  // const handleFilterChange = (category: string, key: string) => {
  //   setCurrentPage(1);

  //   setSelectedFilters((prev) => {
  //     const prevValues = prev[category] || [];
  //     const updated = prevValues.includes(key)
  //       ? prevValues.filter((val) => val !== key)
  //       : [...prevValues, key];

  //     const newFilters = {
  //       ...prev,
  //       [category]: updated,
  //     };

  //     const updatedParams = new URLSearchParams();

  //     // Stream, City, Education Mode go into pathname
  //     const stream = newFilters.stream?.[0] || "list-of-colleges-in-india-poc";
  //     const city = newFilters.city?.[0] || "india";
  //     const mode = newFilters.education_mode?.[0] || "educationMode";

  //     const basePath =
  //       stream === "list-of-colleges-in-india-poc"
  //         ? "/list-of-colleges-in-india-poc"
  //         : `/${streams}/${city}/${mode}-colleges`;

  //     // Others go as query params
  //     Object.entries(newFilters).forEach(([key, values]) => {
  //       if (!["streams", "city", "educationMode"].includes(key)) {
  //         values.forEach((val) => updatedParams.append(key, val));
  //       }
  //     });

  //     // Update URL
  //     navigate(`${basePath}?${updatedParams.toString()}`, { replace: true });

  //     return newFilters;
  //   });
  // };

  const handleTabChange = (stream: string) => {
    setCurrentPage(1);
    setActiveStream(stream);
    // setSelectedFilters((prev) => {
    //   const updated = { ...prev };
    //   delete updated["stream"];
    //   return updated;
    // });
  };

  const handleSearch = () => {
    setCurrentPage(1);
    setSearchTrigger((prev) => !prev);
  };
  const scroll = (direction: "left" | "right") => {
    if (!scrollContainerRef.current) return;
    const scrollAmount = 200;
    scrollContainerRef.current.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage]);
  const handleViewOnMaps = () => {
    navigate("/map-view");
  };
  const handleSortChange = (sortKey: string) => {
    setSortBy(sortKey);
  };
  const sortOptionsMapped = sortOptions.map((opt) => ({
    name: opt.label,
    key: opt.value,
    count: 0,
  }));
  const noSearchCategories = [
    "fee",
    "scholarship",
    "genderDiversity",
    "education_mode",
    "minority_affiliation",
    "sortBy",
    "rankingAndAccreditations",
    "entranceExam",
    "degree",
  ];
  return (
    <>
      <div className="list-of-colleges-in-india-banner-section">
        <div className="bg-gradient-to-r from-[#0B3D8C] to-[#06AFA9]">
          <div className="container max-w-screen-2xl text-white py-8 md:py-16">
            <div className="flex flex-col md:flex-row gap-8">
              <div className="relative w-full md:w-[70%] flex flex-col justify-center ">
                <div className="md:mb-10">
                  <Breadcrumbs
                    links={[
                      { name: "Home", path: "/" },
                      {
                        name: "List of colleges in India",
                        path: "/colleges/list-of-colleges-in-india",
                      },
                    ]}
                  />
                </div>
                <h1 className="text-4xl md:text-5xl min-max-xss:mt-6 min-max-xs:mt-6 font-bold line-clamp-2">
                  {"List of Colleges in India"}
                </h1>
                <p className="text-lg md:text-xl mt-4 mb-auto line-clamp-3">
                  {
                    "India offers a diverse range of colleges across various educational levels, from undergraduate to postgraduate and doctoral programs."
                  }
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-[#F4F7FB] py-10 max-sm:py-8">
        <div className="container mx-auto">
          <div className="flex flex-col lg:gap-11">
            <StreamTabs
              streams={streams}
              activeStream={activeStream}
              handleTabChange={handleTabChange}
              scroll={scroll}
              scrollContainerRef={scrollContainerRef}
            />

            <div className="flex flex-col gap-5">
              <div className="flex flex-col lg:flex-row gap-4 items-center">
                <div className="w-full lg:w-1/4 flex justify-between">
                  <button
                    className="hidden lg:flex p-1 items-center bg-[#EFEFFC] border-blue border rounded"
                    onClick={handleViewOnMaps}
                  >
                    <span>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="33"
                        height="33"
                        viewBox="0 0 33 33"
                        fill="none"
                      >
                        <path
                          d="M16.1717 3.78125H5.15625C4.79158 3.78125 4.44184 3.92612 4.18398 4.18398C3.92612 4.44184 3.78125 4.79158 3.78125 5.15625V27.8438C3.78125 28.2084 3.92612 28.5582 4.18398 28.816C4.44184 29.0739 4.79158 29.2188 5.15625 29.2188H27.8438C28.2084 29.2188 28.5582 29.0739 28.816 28.816C29.0739 28.5582 29.2188 28.2084 29.2188 27.8438V5.15625C29.2188 4.79158 29.0739 4.44184 28.816 4.18398C28.5582 3.92612 28.2084 3.78125 27.8438 3.78125H24.4419"
                          stroke="#00B489"
                          strokeWidth="1.25"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M20.3068 2.31494C18.5647 2.31498 16.894 3.00705 15.6621 4.2389C14.4303 5.47076 13.7383 7.1415 13.7383 8.88359C13.7383 14.0241 18.7665 20.2205 20.0683 21.7389C20.1058 21.7827 20.1524 21.8179 20.2048 21.8419C20.2573 21.866 20.3143 21.8783 20.372 21.878C20.4297 21.8777 20.4866 21.8648 20.5389 21.8403C20.5911 21.8157 20.6373 21.7801 20.6743 21.7359C21.9541 20.2125 26.8756 14.0207 26.8756 8.88359C26.8756 8.02097 26.7057 7.16679 26.3756 6.36984C26.0455 5.57288 25.5616 4.84874 24.9516 4.23879C24.3417 3.62883 23.6175 3.14499 22.8206 2.81489C22.0236 2.4848 21.1694 2.31491 20.3068 2.31494ZM20.3068 11.3384C19.8213 11.3384 19.3466 11.1945 18.9429 10.9247C18.5392 10.655 18.2245 10.2715 18.0387 9.82297C17.8529 9.37438 17.8043 8.88078 17.899 8.40457C17.9938 7.92835 18.2276 7.49093 18.5709 7.1476C18.9143 6.80428 19.3517 6.57048 19.8279 6.47576C20.3041 6.38104 20.7977 6.42967 21.2463 6.61549C21.6949 6.80131 22.0783 7.11597 22.348 7.51969C22.6178 7.92341 22.7617 8.39805 22.7617 8.88359C22.7617 9.53467 22.5031 10.1591 22.0427 10.6194C21.5823 11.0798 20.9579 11.3384 20.3068 11.3384Z"
                          stroke="#00B489"
                          strokeWidth="1.25"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M23.9182 17.2272L29.2188 16.4104"
                          stroke="#00B489"
                          strokeWidth="1.25"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M3.78125 20.3306L17.4073 18.2307"
                          stroke="#00B489"
                          strokeWidth="1.25"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M12.3133 19.0159L9.74219 3.78125"
                          stroke="#00B489"
                          strokeWidth="1.25"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                    View on Maps
                  </button>
                </div>
                <div className="flex flex-wrap gap-4 items-center justify-between w-full lg:w-3/4">
                  <div className="flex gap-2 w-full justify-between">
                    <div className="lg:hidden flex justify-end">
                      <button
                        className="p-2 border bg-white rounded text-[#7D7B89]"
                        onClick={() => setIsMobileFilterOpen(true)}
                      >
                        <JamFilter />
                      </button>
                    </div>
                    <div className="w-full lg:max-w-[378px] relative flex items-center">
                      <input
                        value={searchKeyword}
                        onChange={(e) => setSearchKeyword(e.target.value)}
                        placeholder="Search your Dream College"
                        className=" px-4 py-2 rounded text-sm text-black leading-7 w-full"
                      />
                      <button
                        onClick={handleSearch}
                        className="text-[#7D7B89] absolute right-3"
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M11.2544 11.2309L14.5 14.5M13 7C13 10.3137 10.3137 13 7 13C3.68629 13 1 10.3137 1 7C1 3.68629 3.68629 1 7 1C10.3137 1 13 3.68629 13 7Z"
                            stroke="#7D7B89"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </svg>
                      </button>
                    </div>

                    <div className="hidden lg:flex items-center gap-2">
                      <span className="text-sm font-normal text-gray flex items-center gap-1 text-[#6A6777]">
                        <svg
                          width="18"
                          height="14"
                          viewBox="0 0 18 14"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M4.56699 0.292791C4.4166 0.105317 4.21265 0 4 0C3.78735 0 3.5834 0.105317 3.43301 0.292791L0.225123 4.29285C0.0790376 4.48146 -0.00179691 4.73406 3.03165e-05 4.99626C0.00185754 5.25846 0.0862002 5.50928 0.234892 5.69469C0.383584 5.8801 0.584728 5.98527 0.795003 5.98755C1.00528 5.98983 1.20786 5.88903 1.35911 5.70687L3.19803 3.41384V13C3.19803 13.2652 3.28252 13.5196 3.43292 13.7071C3.58332 13.8946 3.7873 14 4 14C4.2127 14 4.41668 13.8946 4.56708 13.7071C4.71748 13.5196 4.80197 13.2652 4.80197 13V3.41384L6.64089 5.70687C6.79214 5.88903 6.99472 5.98983 7.205 5.98755C7.41527 5.98527 7.61642 5.8801 7.76511 5.69469C7.9138 5.50928 7.99814 5.25846 7.99997 4.99626C8.0018 4.73406 7.92096 4.48146 7.77488 4.29285L4.56699 0.292791Z"
                            fill="#828282"
                          />
                          <path
                            d="M14.567 13.7072C14.4166 13.8947 14.2127 14 14 14C13.7873 14 13.5834 13.8947 13.433 13.7072L10.2251 9.70715C10.079 9.51854 9.9982 9.26594 10 9.00374C10.0019 8.74154 10.0862 8.49072 10.2349 8.30531C10.3836 8.1199 10.5847 8.01473 10.795 8.01245C11.0053 8.01017 11.2079 8.11097 11.3591 8.29313L13.198 10.5862V1.00002C13.198 0.734795 13.2825 0.480436 13.4329 0.292897C13.5833 0.105358 13.7873 0 14 0C14.2127 0 14.4167 0.105358 14.5671 0.292897C14.7175 0.480436 14.802 0.734795 14.802 1.00002V10.5862L16.6409 8.29313C16.7921 8.11097 16.9947 8.01017 17.205 8.01245C17.4153 8.01473 17.6164 8.1199 17.7651 8.30531C17.9138 8.49072 17.9981 8.74154 18 9.00374C18.0018 9.26594 17.921 9.51854 17.7749 9.70715L14.567 13.7072Z"
                            fill="#828282"
                          />
                        </svg>
                        Sort By
                      </span>
                      {sortOptions.length > 0 && (
                        <div className="py-2">
                          <SortBy
                            options={sortOptionsMapped}
                            selectedSort={sortBy}
                            onSortChange={handleSortChange}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex flex-col lg:flex-row gap-4">
                <DesktopFilterSidebar
                  filters={filters}
                  selectedFilters={selectedFilters}
                  handleFilterChange={handleFilterChange}
                  searchStates={searchStates}
                  setSearchStates={setSearchStates}
                  showMoreStates={showMoreStates}
                  setShowMoreStates={setShowMoreStates}
                  openAccordions={openAccordions}
                  toggleAccordion={toggleAccordion}
                  setSelectedFilters={setSelectedFilters}
                  setSearchParams={setSearchParams}
                  noSearchCategories={noSearchCategories}
                />
                <MobileFilterSidebar
                  isMobileFilterOpen={isMobileFilterOpen}
                  filters={filters}
                  selectedFilters={selectedFilters}
                  setSelectedFilters={setSelectedFilters}
                  setSearchParams={setSearchParams}
                  searchStates={searchStates}
                  setSearchStates={setSearchStates}
                  showMoreStates={showMoreStates}
                  setShowMoreStates={setShowMoreStates}
                  activeMobileCategory={activeMobileCategory}
                  setActiveMobileCategory={setActiveMobileCategory}
                  setIsMobileFilterOpen={setIsMobileFilterOpen}
                  handleFilterChange={handleFilterChange}
                />

                <main className="w-full lg:w-3/4">
                  {loading ? (
                    <p>Loading colleges...</p>
                  ) : (
                    <>
                      <div className="flex flex-col">
                        <div className="flex flex-wrap gap-3">
                          <h2 className="text-base md:text-xl font-bold mb-4">
                            Showing {totalCount} Colleges
                          </h2>
                          <div className="flex flex-wrap gap-2 mb-4">
                            {activeStream && (
                              <div className="flex items-center border rounded-full px-3 py-1 text-sm bg-white shadow-sm gap-1">
                                <span>
                                  {streams.find((s) => s.name === activeStream)
                                    ?.title || activeStream}
                                </span>
                                <button
                                  className="text-black"
                                  onClick={() => setActiveStream("")}
                                >
                                  ×
                                </button>
                              </div>
                            )}

                            {Object.entries(selectedFilters).map(
                              ([key, values]) =>
                                values.map((val) => (
                                  <div
                                    key={`${key}-${val}`}
                                    className="flex items-center border rounded-full px-3 py-1 text-sm bg-white shadow-sm gap-1"
                                  >
                                    <span>{val}</span>
                                    <button
                                      className="text-black"
                                      onClick={() =>
                                        handleFilterChange(key, val)
                                      }
                                    >
                                      ×
                                    </button>
                                  </div>
                                ))
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col gap-5">
                          <CollegeCards colleges={colleges} />
                        </div>
                        {colleges.length > 0 && (
                          <div className="flex items-center justify-center mt-6">
                            <div className="flex items-center">
                              <button
                                disabled={currentPage === 1}
                                onClick={() =>
                                  setCurrentPage((prev) =>
                                    Math.max(prev - 1, 1)
                                  )
                                }
                                className={`flex items-center gap-2 px-4 py-2 bg-white rounded-l-full ${
                                  currentPage === 1
                                    ? "opacity-60 cursor-not-allowed"
                                    : "text-black "
                                }`}
                              >
                                <LsiconRightOutline className="rotate-180" />{" "}
                                Prev
                              </button>

                              <div className="text-sm font-medium bg-white p-2.5 border-l border-r">
                                {currentPage} of {totalPages}
                              </div>

                              <button
                                disabled={currentPage === totalPages}
                                onClick={() =>
                                  setCurrentPage((prev) =>
                                    Math.min(prev + 1, totalPages)
                                  )
                                }
                                className={`flex items-center gap-2 px-4 py-2 rounded-r-full border-none ${
                                  currentPage === totalPages
                                    ? "text-gray cursor-not-allowed"
                                    : "text-black bg-white"
                                }`}
                              >
                                Next <LsiconRightOutline />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </main>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
